import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import api from '../../../services/api';
import alertService from '../../../utils/alert';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currency';
import { fmtDate, errMsg, downloadFile } from './ui';

const REASON = { return: 'Returned', discount: 'Discount', correction: 'Correction', short_supply: 'Short supply', price_correction: 'Price correction', other: 'Other' };

// Lists credit notes (kind="credit") or debit notes (kind="debit") with void.
export default function NotesList({ kind, refreshKey, onChanged }) {
  const credit = kind === 'credit';
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get(credit ? '/crm/credit-notes' : '/crm/purchases/debit-notes')
      .then((res) => setNotes(res.data.creditNotes || res.data.debitNotes || [])).catch(() => {}).finally(() => setLoading(false));
  }, [credit]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const voidNote = async (n) => {
    if (!(await alertService.confirm('Void this note?', `The ${credit ? 'invoice' : 'bill'} goes back to its previous balance and the ledger entry is reversed.`, 'danger', 'Void'))) return;
    try {
      await api.post(credit ? `/crm/credit-notes/${n._id}/void` : `/crm/purchases/debit-notes/${n._id}/void`);
      alertService.success('Note voided');
      load(); onChanged?.();
    } catch (e) { alertService.error('Could not void', errMsg(e)); }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="flex items-center justify-between p-3 border-b border-[#D8DDE6]">
        <p className="text-[13px] text-[#706E6B]">{credit ? 'Credit notes reduce what a customer owes on an invoice.' : 'Debit notes reduce what you owe a vendor on a bill (returns, short supply, price corrections).'}</p>
        <button type="button" onClick={load} aria-label="Refresh" className="h-8 w-8 flex items-center justify-center rounded border border-[#D8DDE6]"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /></button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px] sm:min-w-[47.5rem]">
          <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr className="border-b border-[#D8DDE6]">
            <th className="text-left font-semibold px-4 py-2.5">Note</th><th className="text-left font-semibold px-4 py-2.5">Date</th>
            <th className="text-left font-semibold px-4 py-2.5">{credit ? 'Customer' : 'Vendor'}</th><th className="text-left font-semibold px-4 py-2.5">{credit ? 'Invoice' : 'Bill'}</th>
            <th className="text-left font-semibold px-4 py-2.5">Reason</th><th className="text-right font-semibold px-4 py-2.5">Amount</th><th className="px-4 py-2.5" />
          </tr></thead>
          <tbody>
            {notes.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#706E6B]">{loading ? 'Loading…' : `No ${credit ? 'credit' : 'debit'} notes yet.`}</td></tr>}
            {notes.map((n) => (
              <tr key={n._id} className={cn('border-b border-[#F3F2F2]', n.status === 'void' && 'opacity-50')}>
                <td className="px-4 py-2.5 font-semibold">{n.creditNoteNumber || n.debitNoteNumber}</td>
                <td className="px-4 py-2.5">{fmtDate(n.date)}</td>
                <td className="px-4 py-2.5">{credit ? (n.accountId?.name || n.contactId?.name || '—') : (n.vendorId?.name || '—')}</td>
                <td className="px-4 py-2.5">{credit ? n.invoiceId?.invoiceNumber : n.billId?.billNumber}</td>
                <td className="px-4 py-2.5">{REASON[n.reason] || n.reason}</td>
                <td className={cn('px-4 py-2.5 text-right tabular-nums font-semibold', n.status === 'void' && 'line-through')}>{formatCurrency(n.totalAmount, n.currency)}</td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  {!credit && <button type="button" onClick={() => downloadFile(`/crm/purchases/debit-notes/${n._id}/pdf`, `Debit-note-${n.debitNoteNumber}.pdf`)} className="text-[12px] font-semibold text-[#0176D3] mr-3">PDF</button>}
                  {n.status === 'issued'
                  ? <button type="button" onClick={() => voidNote(n)} className="text-[12px] font-semibold text-[#BA0517]">Void</button>
                  : <span className="text-[11px] text-[#706E6B]">Void</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
